'use client'

import type {ImageField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useCallback, useEffect, useRef, useState} from 'react'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

export type Tile = {
  heading: string
  copy: RichTextField
  secondaryCopy: RichTextField
  image: ImageField
}

export type DefaultTiles3WithHoverDescriptionProps = {
  sliceType: string
  sliceVariation: string
  paddingTop?: string | null
  paddingBottom?: string | null
  background?: string | null
  tileNumbering?: string | null
  eyebrow: string
  heading: RichTextField
  description: RichTextField
  tiles: Tile[]
}

const bgMap: Record<string, string> = {
  'Dark mode': 'bg-green-dark',
  'Light mode grey': 'bg-moss-grey-100',
  'Light mode white': 'bg-moss-white',
}

const TileCard = ({
  tile,
  index,
  isDark,
  isNumbered,
  minHeight,
  onMeasure,
}: {
  tile: Tile
  index: number
  isDark: boolean
  isNumbered: boolean
  minHeight: number
  onMeasure: (index: number, height: number) => void
}) => {
  const articleRef = useRef<HTMLElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const touchStartRef = useRef<{x: number; y: number} | null>(null)

  const hasCopy = isFilled.richText(tile.copy)
  const hasSecondaryCopy = isFilled.richText(tile.secondaryCopy)
  const hasImage = isFilled.image(tile.image)
  const hasExpandableContent = (hasCopy || hasSecondaryCopy) && hasImage

  // Measure collapsed height once on mount
  useEffect(() => {
    if (articleRef.current) {
      onMeasure(index, articleRef.current.offsetHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- measure only on mount
  }, [])

  return (
    <article
      ref={articleRef}
      style={{minHeight: minHeight || undefined}}
      className={cn(
        'group/tile flex w-[308px] shrink-0 snap-center flex-col overflow-hidden rounded-lg border lg:w-auto lg:snap-align-none',
        hasExpandableContent ? 'max-lg:cursor-pointer' : '',
        {
          'border-moss-white/10 bg-moss-white/[0.06]': isDark,
          'border-black/[0.08] bg-black/[0.03]': !isDark,
        },
      )}
      onTouchStart={(e) => {
        touchStartRef.current = {x: e.touches[0].clientX, y: e.touches[0].clientY}
      }}
      onTouchEnd={(e) => {
        if (!hasExpandableContent || !touchStartRef.current) {
          return
        }

        const dx = Math.abs(e.changedTouches[0].clientX - touchStartRef.current.x)
        const dy = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y)

        if (dx < 30 && dy < 30) {
          setIsExpanded((prev) => !prev)
        }

        touchStartRef.current = null
      }}
      onClick={() => {
        if (!hasExpandableContent) {
          return
        }

        if (!('ontouchstart' in window)) {
          setIsExpanded((prev) => !prev)
        }
      }}
    >
      <div className="flex flex-1 flex-col p-6">
        {isNumbered && (
          <div
            className={cn('mb-3 text-[13px] font-semibold', {
              'text-green-neon': isDark,
              'text-green-deep': !isDark,
            })}
          >
            {String(index + 1).padStart(2, '0')}
          </div>
        )}

        {tile.heading && (
          <h3 className={cn('text-h3 mb-4', isDark ? 'text-moss-white' : 'text-moss-black')}>{tile.heading}</h3>
        )}

        {(hasCopy || hasSecondaryCopy) && (
          <div
            className={cn('flex flex-1 flex-col gap-4 overflow-hidden transition-all duration-300', {
              'mt-4 max-h-[500px] opacity-100': !hasImage,
              'max-h-0 opacity-0': hasImage && !isExpanded,
              'max-lg:mt-4 max-lg:max-h-[500px] max-lg:opacity-100': hasImage && isExpanded,
              'lg:max-h-0 lg:opacity-0 lg:group-hover/tile:mt-4 lg:group-hover/tile:max-h-[500px] lg:group-hover/tile:opacity-100':
                hasImage,
            })}
          >
            {hasCopy && (
              <PrismicRichText
                field={tile.copy}
                components={{
                  paragraph: ({children}) => (
                    <p className={cn('text-16 leading-[1.55]', isDark ? 'text-moss-white/[85%]' : 'text-black/75')}>
                      {children}
                    </p>
                  ),
                  list: ({children}) => <ul className="flex flex-col gap-2">{children}</ul>,
                  listItem: ({children}) => (
                    <li className="flex items-start gap-3">
                      <span className="mt-2 size-[5px] shrink-0 rounded-[1px] bg-moss-grey-300" />
                      <span
                        className={cn('text-16 leading-[1.55]', isDark ? 'text-moss-white/[85%]' : 'text-black/75')}
                      >
                        {children}
                      </span>
                    </li>
                  ),
                }}
              />
            )}

            {hasSecondaryCopy && (
              <div
                className={cn('mt-auto border-t pt-4', {
                  'border-moss-white/10': isDark,
                  'border-black/10': !isDark,
                })}
              >
                <PrismicRichText
                  field={tile.secondaryCopy}
                  components={{
                    paragraph: ({children}) => (
                      <p
                        className={cn(
                          'text-16 leading-[1.55]',
                          isDark ? 'text-moss-white/[85%]' : 'text-moss-grey-500',
                        )}
                      >
                        {children}
                      </p>
                    ),
                    list: ({children}) => <ul className="mt-2 flex flex-col gap-2">{children}</ul>,
                    listItem: ({children}) => (
                      <li className="flex items-start gap-3">
                        <span className="mt-2 size-[5px] shrink-0 rounded-[1px] bg-moss-grey-300" />
                        <span
                          className={cn(
                            'text-16 leading-[1.55]',
                            isDark ? 'text-moss-white/[85%]' : 'text-moss-grey-500',
                          )}
                        >
                          {children}
                        </span>
                      </li>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {hasImage && (
        <PrismicNextImage
          field={tile.image}
          className="w-full object-cover"
          sizes="(max-width: 768px) 280px, (max-width: 1024px) 280px, 33vw"
        />
      )}
    </article>
  )
}

export const DefaultTiles3WithHoverDescription = ({
  sliceType,
  sliceVariation,
  paddingTop,
  paddingBottom,
  background,
  tileNumbering,
  eyebrow,
  heading,
  description,
  tiles,
}: DefaultTiles3WithHoverDescriptionProps) => {
  const isDark = background === 'Dark mode'
  const isNumbered = tileNumbering === 'Numbered'
  const [tileHeights, setTileHeights] = useState<Record<number, number>>({})

  const handleMeasure = useCallback((index: number, height: number) => {
    setTileHeights((prev) => {
      if (prev[index] === height) {
        return prev
      }

      return {...prev, [index]: height}
    })
  }, [])

  const maxCollapsedHeight = Object.values(tileHeights).length > 0 ? Math.max(...Object.values(tileHeights)) : 0

  const bgClass = bgMap[background ?? 'Light mode white'] ?? 'bg-moss-white'
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? 'X-Large (D:128px / M:64px)'] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  const hasEyebrow = !!eyebrow
  const hasHeading = isFilled.richText(heading)
  const hasDescription = isFilled.richText(description)

  if (tiles.length === 0 && !hasHeading) {
    return null
  }

  return (
    <section
      data-slice-type={sliceType}
      data-slice-variation={sliceVariation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, paddingTopClass, paddingBottomClass, 'px-5 md:px-10 lg:px-5 xl:px-0')}
    >
      <div className="mx-auto max-w-container">
        {(hasEyebrow || hasHeading || hasDescription) && (
          <div className="mb-14">
            {hasEyebrow && (
              <p
                className={cn('mb-4 text-[13px] font-semibold uppercase tracking-widest', {
                  'text-green-neon': isDark,
                  'text-green-deep': !isDark,
                })}
              >
                {eyebrow}
              </p>
            )}
            {hasHeading && (
              <PrismicRichText
                field={heading}
                components={{
                  heading2: ({children}) => (
                    <h2
                      className={cn('text-h3 md:text-h2 font-normal', {
                        'text-moss-grey-300': isDark,
                        'text-grey-550': !isDark,
                      })}
                    >
                      {children}
                    </h2>
                  ),
                  strong: ({children}) => (
                    <strong className={cn('font-normal', isDark ? 'text-moss-white' : 'text-moss-black')}>
                      {children}
                    </strong>
                  ),
                }}
              />
            )}
            {hasDescription && (
              <div className="mt-5 max-w-[748px]">
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({children}) => (
                      <p
                        className={cn('mb-4 text-[19px] leading-relaxed last:mb-0', {
                          'text-moss-white/[85%]': isDark,
                          'text-black/75': !isDark,
                        })}
                      >
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
            )}
          </div>
        )}

        {tiles.length > 0 && (
          <div className="flex snap-x snap-mandatory items-start gap-4 overflow-x-auto scroll-smooth pb-4 lg:grid lg:snap-none lg:grid-cols-3 lg:overflow-visible lg:pb-0">
            {tiles.map((tile, index) => (
              <TileCard
                key={index}
                tile={tile}
                index={index}
                isDark={isDark}
                isNumbered={isNumbered}
                minHeight={maxCollapsedHeight}
                onMeasure={handleMeasure}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}
